import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/features/auth/AuthProvider";
import { facilitiesApi } from "./api";
import type { Facility } from "./types";

/**
 * Centralised role check for a single facility.
 *
 * Returns `isOwner` (the only role that can mutate items / reports /
 * tasks / settings) and `isMaintainer` (read-only + completions only).
 * `unknown` covers the case where the facility query is still loading
 * or the user has no membership at all.
 *
 * Pages should default to the read-only experience until the query
 * resolves, then conditionally render owner-only controls.
 */
export function useFacilityRole(facilityId: number): {
  facility: Facility | undefined;
  isOwner: boolean;
  isMaintainer: boolean;
  isLoading: boolean;
} {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["facility", facilityId],
    queryFn: () => facilitiesApi.get(facilityId),
    enabled: !Number.isNaN(facilityId),
  });
  const facility = q.data;
  const isOwner = Boolean(facility && user && facility.owner === user.id);
  // If we can read the facility at all, we're a member. Anything not the
  // owner is a maintainer in the current role model.
  const isMaintainer = Boolean(facility && !isOwner);
  return { facility, isOwner, isMaintainer, isLoading: q.isLoading };
}
